import { inject, Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, map, of, switchMap } from 'rxjs';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import {
    loadEventLogDetails,
    loadEventLogDetailsCompletion,
    loadEventLogDetailsSuccess,
    loadRecordLogs,
    loadRecordLogsCompletion,
    loadRecordLogsSuccess
} from './integration-event-log-details.actions';
import { Store } from '@ngrx/store';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { selectEventLogDetails, selectRecordLogsDetails } from './integration-event-log-details.selectors';

@Injectable()
export class IntegrationEventLogDetailsEffects {
    getAllIntegrationEventLogs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadEventLogDetails),
            concatLatestFrom(() => this.store.select(selectEventLogDetails)),
            switchMap(([action, logDetails]) => {
                return !logDetails || logDetails.instrumentationId !== action.eventLogId
                    ? this.integrationManagerService.getIntegrationEventLogDetails(action.eventLogId).pipe(
                          map((details) => loadEventLogDetailsSuccess({ eventLogDetails: details })),
                          catchError(() => this.showError('Error loading event log details'))
                      )
                    : of(loadEventLogDetailsCompletion());
            })
        );
    });

    getIntegrationRecordLogs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadRecordLogs),
            concatLatestFrom(() => this.store.select(selectRecordLogsDetails)),
            switchMap(([action, recordLogs]) => {
                const isSameRequest =
                    recordLogs &&
                    recordLogs.eventLogId === action.eventLogId &&
                    recordLogs.recordKey === action.recordKey;

                return !isSameRequest
                    ? this.integrationManagerService.getIntegrationRecordLogs(action.eventLogId, action.recordKey).pipe(
                          map((details) => loadRecordLogsSuccess({ recordLogs: details })),
                          catchError(() => this.showRecordLogsError('Error loading record logs'))
                      )
                    : of(loadRecordLogsCompletion());
            })
        );
    });

    showError = (message: string) =>
        concat(
            of(
                showToasterMessage({
                    message
                })
            ),
            of(loadEventLogDetailsCompletion())
        );

    showRecordLogsError = (message: string) =>
        concat(
            of(
                showToasterMessage({
                    message
                })
            ),
            of(loadRecordLogsCompletion())
        );

    store = inject(Store);

    constructor(
        private readonly actions$: Actions,
        private readonly integrationManagerService: IntegrationManagerService
    ) {}
}
